import { formatEurSigned, formatPct } from '@/lib/format'
import type { ClusterDriftItem, DriftSeverity } from '@/types/rebalance'
import { DriftBadge } from './DriftBadge'

const SEVERITY_RANK: Record<DriftSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

interface ClusterDriftSectionProps {
  drifts: ClusterDriftItem[]
}

export function ClusterDriftSection({ drifts }: ClusterDriftSectionProps) {
  const sorted = [...drifts].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])

  return (
    <section className="hairline mb-6 flex flex-col gap-3 border bg-panel p-4 md:p-6">
      <header className="flex items-baseline justify-between">
        <h2 className="tracked-caps text-xs text-muted">Cluster-Drift</h2>
        <span className="text-xs text-muted">({drifts.length})</span>
      </header>

      {drifts.some((d) => d.has_overlap_with_position_drift) && (
        <div className="hairline border border-bernstein/30 bg-bernstein/5 px-4 py-2 text-[11px] text-bernstein-dim">
          ⚠ {drifts.filter((d) => d.has_overlap_with_position_drift).length} Cluster überschneiden
          sich mit Positionen aus der Position-Drift-Liste. Cluster-Aktion ggf. um Position-Aktion
          reduzieren.
        </div>
      )}

      {/* Desktop: table — visible from md: */}
      <table className="hidden w-full text-xs md:table">
        <thead className="bg-void/50 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Cluster</th>
            <th className="px-3 py-2 text-right">Min</th>
            <th className="px-3 py-2 text-right">Max</th>
            <th className="px-3 py-2 text-right">Actual</th>
            <th className="px-3 py-2 text-right">Drift</th>
            <th className="px-3 py-2 text-right">Severity</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <tr key={d.cluster_name} className="hairline-t border-t">
              <td className="px-3 py-2 text-ink">{d.label}</td>
              <td className="px-3 py-2 text-right text-ink">
                {d.target_min_pct ? formatPct(d.target_min_pct, 1) : '—'}
              </td>
              <td className="px-3 py-2 text-right text-ink">
                {d.target_max_pct ? formatPct(d.target_max_pct, 1) : '—'}
              </td>
              <td className="px-3 py-2 text-right text-ink">{formatPct(d.actual_pct, 1)}</td>
              <td className="px-3 py-2 text-right">
                {d.drift_pct === '0' ? (
                  <span className="text-muted">—</span>
                ) : (
                  <>
                    <span
                      className={d.severity === 'critical' ? 'text-loss' : 'text-bernstein-dim'}
                    >
                      {formatPct(d.drift_pct, 1)}
                    </span>
                    <span
                      className={`block text-[11px] ${
                        d.severity === 'critical' ? 'text-loss' : 'text-bernstein-dim'
                      }`}
                    >
                      {formatEurSigned(d.drift_eur)}
                    </span>
                  </>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <DriftBadge severity={d.severity} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: card-per-row — visible below md: */}
      <ul className="flex flex-col gap-3 md:hidden">
        {sorted.map((d) => (
          <li key={d.cluster_name} className="hairline border bg-void/30 p-3 text-xs">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink">{d.label}</span>
              <DriftBadge severity={d.severity} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-muted">
              <dt>Min</dt>
              <dd className="text-right text-ink">
                {d.target_min_pct ? formatPct(d.target_min_pct, 1) : '—'}
              </dd>
              <dt>Max</dt>
              <dd className="text-right text-ink">
                {d.target_max_pct ? formatPct(d.target_max_pct, 1) : '—'}
              </dd>
              <dt>Actual</dt>
              <dd className="text-right text-ink">{formatPct(d.actual_pct, 1)}</dd>
              <dt>Drift</dt>
              <dd className="text-right">
                {d.drift_pct === '0' ? (
                  <span className="text-muted">—</span>
                ) : (
                  <>
                    <div className={d.severity === 'critical' ? 'text-loss' : 'text-bernstein-dim'}>
                      {formatPct(d.drift_pct, 1)}
                    </div>
                    <div
                      className={`text-[11px] ${
                        d.severity === 'critical' ? 'text-loss' : 'text-bernstein-dim'
                      }`}
                    >
                      {formatEurSigned(d.drift_eur)}
                    </div>
                  </>
                )}
              </dd>
            </dl>
          </li>
        ))}
      </ul>
    </section>
  )
}
